import type { FichaResponse } from '@triade/contracts';
import { formatarCusto, formatarData, formatarDataHora } from '@/lib/format';
import { cn } from '@/lib/utils';

export interface VersoesFichaProps {
  /** Versoes do item (mais nova primeiro), como a API devolveu. */
  versoes: FichaResponse[];
  atualId: string;
  /** Ha edicao nao salva: abrir outra versao ou escolher fica bloqueado (a tela avisa antes de sair). */
  sujo: boolean;
  ocupado?: boolean;
  onAbrir: (id: string) => void;
  onEscolher: (id: string) => void;
  onNova: () => void;
  /** ficha.editar: sem ela nao ha "nova versao" nem "marcar como escolhida". Padrao: sim. */
  podeEditar?: boolean;
  /** custo.ver: sem ele a API nao manda o custo da versao e a tela nao o mostra. Padrao: sim. */
  comCusto?: boolean;
}

function Chip({ children, tom }: { children: React.ReactNode; tom: 'ok' | 'neutro' | 'aviso' }) {
  return (
    <span
      className={cn(
        'rounded-full px-2 py-px text-caption font-semibold',
        tom === 'ok' && 'bg-success-bg text-success-ink',
        tom === 'neutro' && 'bg-line-soft text-ink-body',
        tom === 'aviso' && 'bg-warning-bg text-warning-ink',
      )}
    >
      {children}
    </span>
  );
}

/**
 * Custo de HOJE de uma versao (Sessao 49, C) e, em texto menor, o custo da gravacao. So quando a API mandou `custoHoje`
 * (com custo.ver). Null = a versao nao calcula hoje: "—" com o motivo que a API deu (sem R$).
 */
function CustoHoje({ v }: { v: FichaResponse }) {
  const motivo = (v.alertasCusto ?? []).map((a) => a.message).join(' ');
  return (
    <span className="ml-auto flex flex-col items-end text-right">
      {v.custoHoje === null || v.custoHoje === undefined ? (
        <span className="text-small font-medium text-ink-muted" title={motivo || undefined} data-testid="versao-custo-hoje">
          —
        </span>
      ) : (
        <span className="tabular text-small font-semibold text-ink" data-testid="versao-custo-hoje">
          {formatarCusto(v.custoHoje)}
        </span>
      )}
      {v.custoHoje === null && motivo && <span className="text-caption text-warning-ink">{motivo}</span>}
      {v.custoNaGravacao !== undefined && (
        <span className="tabular text-caption text-ink-muted" data-testid="versao-custo-gravacao">
          na gravação: {formatarCusto(v.custoNaGravacao)} em {formatarData(v.custoNaGravacaoEm)}
        </span>
      )}
    </span>
  );
}

/** Versoes da ficha do item: abrir, marcar como escolhida (a que o motor usa) e criar nova a partir da atual. Presentacional. */
export function VersoesFicha({ versoes, atualId, sujo, ocupado = false, onAbrir, onEscolher, onNova, podeEditar = true, comCusto = true }: VersoesFichaProps) {
  return (
    <section aria-label="Versões da ficha" className="rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <h2 className="text-title font-semibold">Versões</h2>
          <div className="text-small text-ink-muted">Só a versão escolhida entra na formação de preço e no orçamento</div>
        </div>
        {podeEditar && (
          <button
            type="button"
            onClick={onNova}
            disabled={sujo || ocupado}
            title={sujo ? 'Salve ou desfaça as alterações antes' : 'Nova versão a partir da atual'}
            className="h-[34px] rounded-[9px] border border-border bg-card px-3 text-small font-medium hover:bg-line-soft disabled:pointer-events-none disabled:opacity-50"
          >
            Nova versão a partir desta
          </button>
        )}
      </div>
      <ul className="divide-y divide-line-soft">
        {versoes.map((v) => {
          const atual = v.id === atualId;
          return (
            <li key={v.id} data-testid="versao-ficha" aria-current={atual || undefined} className={cn('flex flex-wrap items-center gap-x-3 gap-y-1 px-5 py-3', atual && 'bg-surface-muted')}>
              <button
                type="button"
                onClick={() => onAbrir(v.id)}
                disabled={atual || sujo}
                className="font-semibold text-ink hover:underline disabled:pointer-events-none"
                aria-label={`Abrir versão ${v.versao}`}
              >
                Versão {v.versao}
              </button>
              {v.descricao && <span className="min-w-0 truncate text-small text-ink-secondary">{v.descricao}</span>}
              {atual && <Chip tom="neutro">Aberta</Chip>}
              {v.escolhida && <Chip tom="ok">Escolhida</Chip>}
              {v.emUso && <Chip tom="aviso">Em uso</Chip>}
              {comCusto && v.custoHoje !== undefined && <CustoHoje v={v} />}
              {comCusto && v.custoHoje === undefined && v.custoUnitario !== undefined && <span className="tabular ml-auto text-small font-medium text-ink">{formatarCusto(v.custoUnitario)}</span>}
              {comCusto && v.custoHoje === undefined && v.custoCalculadoEm !== undefined && <span className="text-caption text-ink-muted">{formatarDataHora(v.custoCalculadoEm)}</span>}
              {podeEditar && !v.escolhida && (
                <button
                  type="button"
                  onClick={() => onEscolher(v.id)}
                  disabled={sujo || ocupado}
                  aria-label={`Marcar versão ${v.versao} como escolhida`}
                  className="h-7 rounded-lg border border-border bg-card px-2.5 text-caption font-medium hover:bg-line-soft disabled:pointer-events-none disabled:opacity-50"
                >
                  Marcar como escolhida
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
